import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { 
  Mic, 
  QrCode, 
  LayoutDashboard, 
  ChefHat, 
  Bell,
  ArrowRight,
  Sparkles
} from "lucide-react";

export const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  // Auto-redirect to dashboard if already logged in
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const features = [
    {
      icon: <QrCode className="w-8 h-8" />,
      title: "QR Menü",
      description: "Müşterileriniz telefonlarıyla QR kodu okutarak menünüze anında ulaşsın"
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Sesli Sipariş",
      description: "Yapay zeka asistanı ile sesli konuşarak sipariş verme deneyimi"
    },
    {
      icon: <LayoutDashboard className="w-8 h-8" />,
      title: "Kolay Yönetim",
      description: "Menünüzü, siparişlerinizi ve ayarlarınızı tek panelden yönetin"
    },
    {
      icon: <Bell className="w-8 h-8" />,
      title: "Anlık Bildirim",
      description: "Yeni siparişlerden sesli bildirimlerle anında haberdar olun"
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1730006077306-7d73a2313bf7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBjaGVmJTIwdGFibGV0JTIwa2l0Y2hlbiUyMGhhcHB5JTIwY291cGxlJTIwZGluaW5nfGVufDB8fHx8MTc3MzYyNzk4Nnww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 via-stone-900/70 to-stone-900/50" />
        </div>

        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white font-['Manrope']">MenuAI</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Button 
              onClick={() => navigate("/auth")}
              className="bg-white text-stone-900 hover:bg-stone-100 rounded-full px-6"
              data-testid="nav-login-btn"
            >
              Giriş Yap
            </Button>
          </motion.div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 px-6 md:px-12 py-20 md:py-32 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Yapay Zeka Destekli
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Manrope'] leading-tight mb-6">
              Restoranınız İçin
              <br />
              <span className="text-orange-400">Akıllı QR Menü</span>
            </h1>
            
            <p className="text-lg md:text-xl text-stone-300 mb-8 max-w-xl leading-relaxed">
              Müşterileriniz yapay zeka asistanıyla sesli konuşarak sipariş versin. 
              Menünüzü kolayca yönetin, siparişleri anında takip edin.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate("/auth")}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-orange-500/30 transition-all active:scale-95"
                data-testid="hero-start-btn"
              >
                Hemen Başla
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/menu/demo")}
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="hero-demo-btn"
              >
                Demo Görüntüle
              </Button>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Features Section */}
      <section className="px-6 md:px-12 py-20 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 font-['Manrope'] mb-4">
            Neden MenuAI?
          </h2>
          <p className="text-stone-600 text-lg max-w-2xl mx-auto">
            Modern restoranlar için tasarlanmış, yapay zeka destekli menü yönetim sistemi
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-stone-100"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-5">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-stone-900 font-['Manrope'] mb-3">
                {feature.title}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 py-20 bg-stone-900">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Manrope'] mb-6">
            Restoranınızı Dijitalleştirin
          </h2>
          <p className="text-stone-400 text-lg mb-8 max-w-2xl mx-auto">
            Hemen kayıt olun ve dakikalar içinde yapay zeka destekli QR menünüzü oluşturun.
          </p>
          <Button 
            onClick={() => navigate("/auth")}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 py-6 text-lg shadow-lg shadow-orange-500/30"
            data-testid="cta-register-btn"
          >
            Ücretsiz Kayıt Ol
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-8 bg-stone-900 border-t border-stone-800">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-stone-400">
            <ChefHat className="w-5 h-5" />
            <span className="font-medium">MenuAI</span>
          </div>
          <p className="text-stone-500 text-sm">
            © 2024 MenuAI. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};
